// ✅ src/pages/VehicleV2/VehicleTypePosition.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/typePosition", typePosition);
//
// Endpoints used:
//   GET  https://nissi-india.com/api/typePosition/view?typePositionId=..
//   POST https://nissi-india.com/api/typePosition/save
//
// ✅ Notes:
// - Uses your common localStorage header pattern
// - Supports add + edit
// - Edit reads: /vehicle/VehicleTypePosition?typePositionId=..
// - Save sends: { typePositionId, typePositionName, loginUserId }

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaPlus } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/typePosition";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const VehicleTypePosition = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Your shared localStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ IMPORTANT: your URL uses typePositionId
  const typePositionId = toInt(searchParams.get("typePositionId"), 0);

  // Rights / BranchActive
  const canAdd = useMemo(() => {
    const v = sessionStorage.getItem("typepos_addrights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const canEdit = useMemo(() => {
    const v = sessionStorage.getItem("typepos_editrights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const branchActive = useMemo(() => {
    const v = sessionStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const [typePositionName, setTypePositionName] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const inputClass = `w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  /* ------------------------- LOAD EDIT ------------------------- */
  const loadEdit = useCallback(async () => {
    if (!typePositionId) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/view`, {
        params: { typePositionId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Record not found");
      }

      const row = data?.data || {};
      setTypePositionName(
        toStr(row.typePositionName || row.tyrePositionType || row.fld_vehicletyre_positiontype)
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load record");
    } finally {
      setPageLoading(false);
    }
  }, [typePositionId, commonHeaders]);

  useEffect(() => {
    loadEdit();
  }, [loadEdit]);

  /* ------------------------- ACTIONS ------------------------- */
  const handleClear = () => {
    if (loading || pageLoading) return;
    setTypePositionName("");
  };

  const handleSave = async () => {
    const value = toStr(typePositionName);

    if (!value) {
      toast.error("Please enter Type Position");
      return;
    }

    if (!branchActive) {
      toast.error("Branch is not active");
      return;
    }

    if (typePositionId > 0 && !canEdit) {
      toast.error("You do not have edit rights");
      return;
    }

    if (typePositionId === 0 && !canAdd) {
      toast.error("You do not have add rights");
      return;
    }

    const loginUserId =
      toInt(sessionStorage.getItem("loginUserId"), 0) ||
      toInt(localStorage.getItem("userId"), 0);

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/save`,
        {
          typePositionId: typePositionId || 0,
          typePositionName: value,
          loginUserId,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Failed to save");
      }

      toast.success(data?.message || "Saved successfully");

      setTimeout(() => {
        navigate("/vehicle/vehicletyrepositionListing");
      }, 700);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TYPE POSITION</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {typePositionId ? "Edit Type Position" : "Add Type Position"}
              </h1>

              {pageLoading && <span className="text-sm opacity-70">Loading...</span>}
            </div>

            <button
              type="button"
              onClick={() => navigate("/vehicle/vehicletyrepositionListing")}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Listing
            </button>
          </div>

          {/* Form */}
          <div
            className={`rounded-xl p-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="max-w-2xl mx-auto">
              <div className="mb-8">
                <label htmlFor="typePositionName" className={labelClass}>
                  Type Position
                </label>

                <input
                  id="typePositionName"
                  type="text"
                  value={typePositionName}
                  onChange={(e) => setTypePositionName(toStr(e.target.value))}
                  placeholder="Enter type position..."
                  disabled={pageLoading || loading}
                  className={`${inputClass} ${
                    pageLoading || loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={loading || pageLoading}
                  className={`px-6 py-3 border rounded-lg font-medium transition-colors ${
                    isDark
                      ? "text-gray-300 border-gray-600 hover:bg-gray-700"
                      : "text-gray-700 border-gray-300 hover:bg-gray-50"
                  } ${loading || pageLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaPlus className="inline mr-2" />
                  New
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={
                    loading ||
                    pageLoading ||
                    !toStr(typePositionName) ||
                    !branchActive ||
                    (typePositionId > 0 ? !canEdit : !canAdd)
                  }
                  className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading ||
                    pageLoading ||
                    !toStr(typePositionName) ||
                    !branchActive ||
                    (typePositionId > 0 ? !canEdit : !canAdd)
                      ? "bg-gray-400 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass}`
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="w-4 h-4" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : VEHICLE TYPE POSITION ENTRY
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypePosition;